import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Briefcase, Camera, Check, Clock3, FolderKanban, Hammer, Siren, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { postWithRetry, formatApiErrorDetail } from "@/lib/api";
import { AREA_OPTIONS, MATERIAL_OPTIONS, TIMELINE_OPTIONS, WORK_TYPES, REQUEST_TYPES } from "@/lib/constants";
import WhatsAppIcon from "./WhatsAppIcon";

const TYPE_ICONS = { PROJECT: FolderKanban, SMALL_JOB: Hammer, URGENT: Siren, B2B_MAINTENANCE: Briefcase };

const STEP_TITLES = {
  type: "Cu ce te putem ajuta?",
  services: "Ce servicii sunt implicate?",
  location: "Unde se află lucrarea?",
  area: "Care este suprafața aproximativă?",
  materials: "Ai nevoie și de materiale?",
  timeline: "Când dorești să începi?",
  company: "Cum se numește firma ta?",
  description: "Descrie-ne pe scurt ce trebuie făcut.",
  contact: "Unde te contactăm?",
};

const STEP_HINTS = {
  services: "Alege una sau mai multe lucrări — un proiect poate necesita mai multe specializări.",
  location: "Orașul sau zona — de exemplu: Cluj-Napoca, județul Cluj.",
  area: "O estimare e suficientă — o putem preciza la evaluare.",
  company: "Pentru mentenanța firmelor, ne ajută să știm cu ce business lucrăm.",
  description: "Câteva fraze sunt suficiente. Poți trimite poze și filmulețe în conversația de WhatsApp, după trimiterea cererii.",
  contact: "Îți răspundem în maximum 15 minute pentru urgențe, respectiv 24 de ore pentru celelalte cereri.",
};

function OptionCard({ label, sub, active, onClick, testid, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      className={`flex min-h-[56px] w-full items-center justify-between gap-3 border px-5 py-4 text-left text-base transition-all duration-200 ${
        active ? "border-copper bg-copper/10 text-paper" : "border-white/15 bg-ink-900 text-paper/85 hover:border-copper/60 hover:bg-ink-800"
      }`}
      data-testid={testid}
    >
      <span className="flex items-center gap-3">
        {Icon && <Icon className={`h-5 w-5 shrink-0 ${active ? "text-copper" : "text-muted-foreground"}`} />}
        <span>
          {label}
          {sub && <span className="block text-xs text-muted-foreground">{sub}</span>}
        </span>
      </span>
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center border transition-all ${active ? "border-copper bg-copper text-ink-950" : "border-white/25"}`}>
        {active && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </span>
    </button>
  );
}

function StepHeader({ nr, total, title, hint }) {
  return (
    <div>
      <p className="eyebrow mb-2">Pasul {nr} din {total}</p>
      <h3 className="display-title text-3xl sm:text-4xl">{title}</h3>
      {hint && <p className="mt-3 text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function QuoteFlowV2() {
  const { quoteOpen, closeQuote, quotePreset, whatsappHref } = useSite();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const emptyForm = {
    request_type: "", services: [], location: "", area: "", materials_option: "",
    timeline: "", description: "", name: "", phone: "", company: "", consent: false,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (quoteOpen) {
      setForm((f) => ({
        ...emptyForm,
        ...f,
        request_type: quotePreset.request_type || "",
        materials_option: quotePreset.materials_needed ? "Manoperă + materiale" : (quotePreset.request_type ? "" : f.materials_option),
      }));
      setStep(0);
      setDir(1);
      setDone(false);
      setError("");
    }
  }, [quoteOpen]);

  const steps = useMemo(() => {
    const s = ["type", "services", "location"];
    if (form.request_type === "PROJECT") s.push("area", "materials", "timeline");
    else if (form.request_type === "SMALL_JOB") s.push("materials");
    else if (form.request_type === "B2B_MAINTENANCE") s.push("company", "timeline");
    s.push("description", "contact");
    return s;
  }, [form.request_type]);

  const total = steps.length;
  const current = steps[step];

  const go = (next) => {
    setDir(next > step ? 1 : -1);
    setStep(next);
  };

  const canContinue = useMemo(() => {
    switch (current) {
      case "type": return Boolean(form.request_type);
      case "services": return form.services.length > 0;
      case "location": return form.location.trim().length >= 2;
      case "area": return Boolean(form.area);
      case "materials": return Boolean(form.materials_option);
      case "timeline": return Boolean(form.timeline);
      case "company": return form.company.trim().length >= 2;
      case "description": return true;
      case "contact": return form.name.trim().length >= 2 && form.phone.trim().length >= 6 && form.consent;
      default: return false;
    }
  }, [current, form]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await postWithRetry("/leads", {
        ...form,
        work_type: form.services[0] || "Altă lucrare",
        materials: form.materials_option,
        materials_needed: form.materials_option === "Manoperă + materiale",
        source: quotePreset.source || "website",
      });
      setDone(true);
      if (waLink) window.open(waLink, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e.response
        ? formatApiErrorDetail(e.response.data?.detail) || e.message
        : "Nu am reușit să trimitem cererea. Verifică conexiunea la internet și încearcă din nou — datele completate nu s-au pierdut.");
    } finally {
      setSubmitting(false);
    }
  };

  const waMessage = useMemo(() => {
    const typeLabel = REQUEST_TYPES.find((t) => t.id === form.request_type)?.label || "Cerere de ofertă";
    const lines = [
      "Bună ziua! Doresc o ofertă BROX CONSTRUCT.",
      `Nume: ${form.name || "—"}`,
      `Telefon: ${form.phone || "—"}`,
      `Localitate: ${form.location || "—"}`,
      `Servicii: ${form.services.join(", ") || "—"}`,
      `Tip lucrare: ${typeLabel}`,
      form.area ? `Suprafață: ${form.area}` : "",
      form.materials_option ? `Materiale: ${form.materials_option}` : "",
      form.company ? `Firma: ${form.company}` : "",
      form.timeline ? `Perioada dorită: ${form.timeline}` : "",
      form.request_type === "URGENT" ? "CARACTER URGENT" : "",
      form.description ? `Descriere: ${form.description}` : "",
      "Pot trimite aici și fotografii/video cu lucrarea.",
    ].filter(Boolean);
    return lines.join("\n");
  }, [form]);

  const waLink = whatsappHref(waMessage);
  const close = () => {
    closeQuote();
    setTimeout(() => { setForm(emptyForm); setStep(0); setDone(false); }, 350);
  };

  const variants = {
    enter: (d) => ({ opacity: 0, x: 40 * d }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: -40 * d }),
  };

  return (
    <AnimatePresence>
      {quoteOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/80 backdrop-blur-sm sm:items-center sm:p-6"
          data-testid="quote-modal"
        >
          <div className="grain relative flex h-[94vh] w-full max-w-2xl flex-col border-t border-white/10 bg-ink-900 sm:h-auto sm:max-h-[88vh] sm:border" data-testid="quote-modal-panel">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 sm:px-8">
              <div className="flex items-center gap-3">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full ${form.request_type === "URGENT" && !done ? "bg-red-500/20 text-red-400" : "bg-wa/15 text-wa"}`}>
                  {form.request_type === "URGENT" && !done ? <Siren className="h-4.5 w-4.5" /> : <WhatsAppIcon className="h-4.5 w-4.5" />}
                </span>
                <div>
                  <p className="font-display text-lg font-bold uppercase tracking-wide leading-none">CERE OFERTĂ</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{form.request_type === "URGENT" && !done ? "Te contactăm în max. 15 min" : "Te contactăm în max. 24h"}</p>
                </div>
              </div>
              <button
                onClick={close}
                className="flex h-10 w-10 items-center justify-center border border-white/15 text-paper/70 transition-colors hover:border-copper hover:text-copper"
                aria-label="Închide"
                data-testid="quote-close-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {!done && (
              <div className="h-0.5 w-full bg-white/10">
                <motion.div
                  className={`h-full ${form.request_type === "URGENT" ? "bg-red-500" : "bg-copper"}`}
                  animate={{ width: `${((step + 1) / total) * 100}%` }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
              {done ? (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex h-full flex-col items-center justify-center py-8 text-center" data-testid="quote-success">
                  <span className={`flex h-16 w-16 items-center justify-center rounded-full text-ink-950 ${form.request_type === "URGENT" ? "bg-red-500" : "bg-copper"}`}>
                    <Check className="h-8 w-8" strokeWidth={3} />
                  </span>
                  <h3 className="display-title mt-6 text-3xl sm:text-4xl">
                    {form.request_type === "URGENT" ? "Cerere urgentă înregistrată" : "Cerere înregistrată"}
                  </h3>
                  {form.request_type === "URGENT" && (
                    <p className="mt-3 border border-red-500/40 bg-red-500/10 px-4 py-2 font-mono text-xs uppercase tracking-wider text-red-400" data-testid="quote-urgent-note">
                      Cererea ta este marcată URGENT în sistemul nostru
                    </p>
                  )}
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
                    Cererea ta a fost înregistrată. Echipa BROX CONSTRUCT te va contacta
                    {form.request_type === "URGENT" ? " în maximum 15 minute" : " în maximum 24 de ore"} pe
                    numărul <span className="text-paper">{form.phone}</span>. Pentru viteză, poți trimite acum
                    detaliile și pozele direct pe WhatsApp.
                  </p>
                  <div className="mt-8 flex w-full max-w-sm flex-col gap-3">
                    {waLink ? (
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary w-full !bg-wa !text-ink-950 hover:!bg-wa-dark"
                        data-testid="quote-whatsapp-continue"
                      >
                        <WhatsAppIcon className="h-5 w-5" />
                        CONTINUĂ PE WHATSAPP
                      </a>
                    ) : (
                      <p className="border border-white/10 bg-ink-800 px-4 py-3 text-xs text-muted-foreground">
                        Numărul de WhatsApp se configurează din panoul de administrare.
                      </p>
                    )}
                    <button onClick={close} className="btn-ghost w-full" data-testid="quote-finish-btn">GATA</button>
                  </div>
                  <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
                    <Camera className="h-4 w-4 text-copper" />
                    Poți trimite poze și filmulețe cu proiectul direct în conversația WhatsApp.
                  </p>
                  <p className="max-w-md text-[11px] leading-relaxed text-muted-foreground/70" data-testid="quote-sitevisit-note">
                    Pentru unele lucrări este necesară o evaluare la fața locului înainte de oferta finală.
                  </p>
                </motion.div>
              ) : (
                <AnimatePresence mode="wait" custom={dir}>
                  <motion.div
                    key={`${current}-${step}`}
                    custom={dir}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="space-y-6"
                  >
                    {current === "type" && (
                      <div>
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.type} />
                        <div className="grid gap-2.5">
                          {REQUEST_TYPES.map((t) => {
                            const Icon = TYPE_ICONS[t.id];
                            return (
                              <OptionCard key={t.id} label={t.label} icon={Icon} active={form.request_type === t.id}
                                testid={`quote-type-option-${t.id.toLowerCase()}`}
                                onClick={() => setForm({ ...form, request_type: t.id })} />
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {current === "services" && (
                      <div>
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.services} hint={STEP_HINTS.services} />
                        <div className="grid gap-2.5 sm:grid-cols-2">
                          {WORK_TYPES.map((t) => (
                            <OptionCard key={t} label={t} active={form.services.includes(t)}
                              testid={`quote-service-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() => setForm({
                                ...form,
                                services: form.services.includes(t) ? form.services.filter((x) => x !== t) : [...form.services, t],
                              })} />
                          ))}
                        </div>
                      </div>
                    )}

                    {current === "location" && (
                      <div className="space-y-6">
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.location} hint={STEP_HINTS.location} />
                        <div>
                          <label htmlFor="quote-location" className="label-brox">Localitatea / zona</label>
                          <input
                            id="quote-location"
                            className="input-brox"
                            placeholder="Ex: Cluj-Napoca"
                            value={form.location}
                            onChange={(e) => setForm({ ...form, location: e.target.value })}
                            data-testid="quote-location-input"
                            autoFocus
                          />
                        </div>
                      </div>
                    )}

                    {current === "area" && (
                      <div>
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.area} hint={STEP_HINTS.area} />
                        <div className="grid gap-2.5">
                          {AREA_OPTIONS.map((a) => (
                            <OptionCard key={a} label={a} active={form.area === a}
                              testid={`quote-option-area-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() => setForm({ ...form, area: a })} />
                          ))}
                        </div>
                      </div>
                    )}

                    {current === "materials" && (
                      <div>
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.materials} />
                        <div className="grid gap-2.5">
                          {MATERIAL_OPTIONS.map((m) => (
                            <OptionCard key={m} label={m} active={form.materials_option === m}
                              testid={`quote-materials-option-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() => setForm({ ...form, materials_option: m })} />
                          ))}
                        </div>
                      </div>
                    )}

                    {current === "timeline" && (
                      <div>
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.timeline} />
                        <div className="grid gap-2.5">
                          {TIMELINE_OPTIONS.map((t) => (
                            <OptionCard key={t} label={t} active={form.timeline === t}
                              testid={`quote-option-timeline-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() => setForm({ ...form, timeline: t })} />
                          ))}
                        </div>
                      </div>
                    )}

                    {current === "company" && (
                      <div className="space-y-6">
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.company} hint={STEP_HINTS.company} />
                        <div>
                          <label htmlFor="quote-company" className="label-brox">Numele firmei</label>
                          <input id="quote-company" className="input-brox" placeholder="Ex: Pensiunea Valea Verde"
                            value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                            data-testid="quote-company-input" autoFocus />
                        </div>
                      </div>
                    )}

                    {current === "description" && (
                      <div className="space-y-6">
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.description} hint={STEP_HINTS.description} />
                        <div>
                          <label htmlFor="quote-description" className="label-brox">Detaliile lucrării</label>
                          <textarea
                            id="quote-description"
                            className="input-brox min-h-[140px] resize-none"
                            placeholder="Ex: renovare completă apartament 3 camere, înlocuire instalații electrice și sanitare..."
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            data-testid="quote-description-input"
                          />
                        </div>
                      </div>
                    )}

                    {current === "contact" && (
                      <div className="space-y-6">
                        <StepHeader nr={step + 1} total={total} title={STEP_TITLES.contact} hint={STEP_HINTS.contact} />
                        <div>
                          <label htmlFor="quote-name" className="label-brox">Numele tău</label>
                          <input id="quote-name" className="input-brox" placeholder="Nume și prenume"
                            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="quote-name-input" />
                        </div>
                        <div>
                          <label htmlFor="quote-phone" className="label-brox">Telefon</label>
                          <input id="quote-phone" type="tel" className="input-brox" placeholder="07xx xxx xxx"
                            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            data-testid="quote-phone-input" />
                        </div>
                        <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-muted-foreground" data-testid="quote-consent-label">
                          <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[#D96B27]"
                            checked={form.consent}
                            onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                            data-testid="quote-consent-checkbox" />
                          <span>
                            Sunt de acord cu prelucrarea datelor personale pentru a fi contactat în legătură cu
                            cererea mea, conform <a href="/politica-confidentialitate" className="text-copper underline underline-offset-2" data-testid="quote-privacy-link">Politicii de confidențialitate</a>.
                          </span>
                        </label>
                        <p className="border border-white/10 bg-ink-800/60 px-4 py-3 text-[11px] leading-relaxed text-muted-foreground" data-testid="quote-disclaimer">
                          Transmiterea formularului reprezintă o solicitare de ofertă și nu constituie acceptarea
                          automată a unei lucrări sau asumarea unei obligații de plată. În funcție de complexitatea
                          proiectului, îți putem cere informații suplimentare, poze, măsurători sau o evaluare la
                          fața locului înainte de oferta finală.
                        </p>
                        {error && (
                          <p className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="quote-error">
                            {error}
                          </p>
                        )}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>

            {!done && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-5 py-4 sm:px-8">
                <button
                  onClick={() => (step === 0 ? close() : go(step - 1))}
                  className="inline-flex min-h-[48px] items-center gap-2 px-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-copper"
                  data-testid="quote-back-btn"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {step === 0 ? "ÎNCHIDE" : "ÎNAPOI"}
                </button>
                {step < total - 1 ? (
                  <button onClick={() => canContinue && go(step + 1)} disabled={!canContinue}
                    className={`btn-primary ${!canContinue ? "cursor-not-allowed opacity-40" : ""}`}
                    data-testid="quote-next-btn">
                    CONTINUĂ
                    <ArrowRight className="h-5 w-5" />
                  </button>
                ) : (
                  <button onClick={submit} disabled={!canContinue || submitting}
                    className={`btn-primary !bg-wa hover:!bg-wa-dark !text-sm sm:!text-base ${!canContinue || submitting ? "cursor-not-allowed opacity-40" : ""}`}
                    data-testid="quote-submit-btn">
                    {submitting ? "SE TRIMITE..." : (
                      <>
                        <WhatsAppIcon className="h-5 w-5" />
                        TRIMITE CEREREA PE WHATSAPP
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
